import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { Modal } from '../../common/Modal';
import { Button } from '../../common/Button';
import { DateTimePicker } from '../../common/DateTimePicker';
import { Loading } from '../../common';

interface RescheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  reservation: {
    id: number;
    room_id: number;
    room_name: string;
    customer_name: string;
    date: string;
    time: string;
  };
  onSuccess: () => void;
}

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
  
  @media (max-width: 767px) {
    gap: ${props => props.theme.spacing.md};
  }
`;

const ReservationInfo = styled.div`
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.lg};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.xs} 0;
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const InfoLabel = styled.span`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
`;

const InfoValue = styled.span`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.primary};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
`;

const DateTimeSection = styled.div`
  padding: ${props => props.theme.spacing.lg};
  background: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.lg};
`;

const SectionTitle = styled.h4`
  margin: 0 0 ${props => props.theme.spacing.md} 0;
  font-size: ${props => props.theme.typography.fontSize.md};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.xl};
  
  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: row;
    justify-content: flex-end;
  }
`;

const ErrorMessage = styled.div`
  padding: ${props => props.theme.spacing.md};
  background: rgba(255, 59, 48, 0.1);
  border: 1px solid rgba(255, 59, 48, 0.3);
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.error};
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const LoadingIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const RescheduleModal: React.FC<RescheduleModalProps> = ({
  isOpen,
  onClose,
  reservation,
  onSuccess,
}) => {
  const [selectedDate, setSelectedDate] = useState(reservation.date);
  const [selectedTime, setSelectedTime] = useState(reservation.time.substring(0, 5)); // HH:MM
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDateChange = useCallback((date: string) => {
    setSelectedDate(date);
    setSelectedTime(''); // Clear time when date changes
    setError(null);
  }, []);

  const handleTimeChange = useCallback((time: string) => {
    setSelectedTime(time);
    setError(null);
  }, []);

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) {
      setError('Por favor selecciona una fecha y hora');
      return;
    }

    // Check if date/time actually changed
    const currentTime = reservation.time.substring(0, 5);
    if (selectedDate === reservation.date && selectedTime === currentTime) {
      setError('Por favor selecciona una fecha u hora diferente');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { adminApi } = await import('../../../services/api');
      await adminApi.updateReservationDateTime(reservation.id, selectedDate, selectedTime);
      
      onSuccess();
      onClose();
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Error al actualizar la fecha';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Cambiar Fecha y Hora">
      <ModalContent>
        <ReservationInfo>
          <InfoRow>
            <InfoLabel>Cliente:</InfoLabel>
            <InfoValue>{reservation.customer_name}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Sala:</InfoLabel>
            <InfoValue>{reservation.room_name}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Fecha actual:</InfoLabel>
            <InfoValue>{formatDate(reservation.date)}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Hora actual:</InfoLabel>
            <InfoValue>{reservation.time.substring(0, 5)}</InfoValue>
          </InfoRow>
        </ReservationInfo>

        <DateTimeSection>
          <SectionTitle>Selecciona nueva fecha y horario</SectionTitle>
          <DateTimePicker
            roomId={reservation.room_id}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            onDateChange={handleDateChange}
            onTimeChange={handleTimeChange}
          />
        </DateTimeSection>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <ButtonGroup>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            fullWidth
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            fullWidth
            disabled={isSubmitting || !selectedDate || !selectedTime}
          >
            {isSubmitting ? (
              <LoadingIndicator>
                <Spinner />
                Actualizando...
              </LoadingIndicator>
            ) : (
              'Confirmar Cambio'
            )}
          </Button>
        </ButtonGroup>
      </ModalContent>
    </Modal>
  );
};
